// src/pages/surveys/SurveyBuilderPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useSurveyBuilderStore } from '../../store/surveyBuilderStore';
import { surveyService } from '../../services/survey.service';
import { projectService } from '../../services/project.service';
import { SurveyBuilderCanvas } from '../../components/surveys/builder';
import { Project } from '../../types/project.types';
import toast from 'react-hot-toast';

const SurveyBuilderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { selectedProjectId } = useProject();
  const { setSurvey, createNewSurvey, survey, hasUnsavedChanges, reset } = useSurveyBuilderStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);

  const currentProjectId = selectedProjectId;

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      if (!currentUser) return;
      try {
        const projectList = await projectService.getUserProjects(currentUser.uid);
        setProjects(projectList);
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    };
    loadProjects();
  }, [currentUser]);

  // Load survey or create new one
  useEffect(() => {
    const loadSurvey = async () => {
      if (!currentUser || !currentProjectId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        if (id === 'new') {
          // Create a new survey
          createNewSurvey(currentProjectId, 'Untitled Survey');
        } else if (id) {
          // Load existing survey
          const surveyData = await surveyService.get(id);
          if (surveyData) {
            setSurvey(surveyData);
          } else {
            setError('Survey not found');
          }
        }
      } catch (err) {
        console.error('Error loading survey:', err);
        setError('Failed to load survey');
      } finally {
        setIsLoading(false);
      }
    };

    loadSurvey();

    // Cleanup on unmount
    return () => {
      reset();
    };
  }, [id, currentUser, currentProjectId, setSurvey, createNewSurvey, reset]);

  // Warn on unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle back navigation with unsaved changes warning
  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/surveys');
      }
    } else {
      navigate('/surveys');
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!survey || !currentUser || !userProfile) return;

    try {
      if (id === 'new') {
        // Create new survey first
        const newSurvey = await surveyService.create(
          {
            projectId: survey.projectId,
            name: survey.name,
            description: survey.description,
            questions: survey.questions,
            settings: survey.settings,
          },
          currentUser.uid,
          userProfile.name || 'User'
        );

        // Now update it with conditionalLogic and skipPatterns if they exist
        if (survey.conditionalLogic.length > 0 || survey.skipPatterns.length > 0) {
          await surveyService.update(newSurvey.id, {
            conditionalLogic: survey.conditionalLogic,
            skipPatterns: survey.skipPatterns,
          });
        }

        toast.success('Survey created');
        // Navigate to the new survey's builder page
        navigate(`/surveys/builder/${newSurvey.id}`, { replace: true });
      } else {
        // Update existing survey
        await surveyService.update(survey.id, {
          name: survey.name,
          description: survey.description,
          projectId: survey.projectId,
          questions: survey.questions,
          conditionalLogic: survey.conditionalLogic,
          skipPatterns: survey.skipPatterns,
          settings: survey.settings,
        });
        toast.success('Survey saved');
      }
      useSurveyBuilderStore.getState().markAsSaved();
    } catch (err) {
      console.error('Error saving survey:', err);
      toast.error('Failed to save survey');
    }
  };

  // Handle publish
  const handlePublish = async () => {
    if (!survey || id === 'new') return;

    try {
      await surveyService.publish(survey.id);
      // Refresh the survey to get updated status
      const updatedSurvey = await surveyService.get(survey.id);
      if (updatedSurvey) {
        setSurvey(updatedSurvey);
      }
      toast.success('Survey published! It can now be assigned to workloads.');
    } catch (err) {
      console.error('Error publishing survey:', err);
      toast.error('Failed to publish survey');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-900">{error}</h2>
          <p className="text-gray-500 mt-2">
            The survey could not be loaded. Please try again.
          </p>
          <button
            onClick={() => navigate('/surveys')}
            className="mt-4 inline-flex items-center text-indigo-600 hover:text-indigo-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Surveys
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          Back
        </button>
        <span className="text-gray-300 mr-4">|</span>
        <span className="text-sm text-gray-500">
          {id === 'new' ? 'New Survey' : 'Edit Survey'}
        </span>
      </div>

      {/* Builder canvas */}
      <div className="flex-1 overflow-hidden">
        <SurveyBuilderCanvas
          onSave={handleSave}
          onPublish={handlePublish}
          isPublished={survey?.status === 'active'}
          projects={projects}
          isNewSurvey={id === 'new'}
        />
      </div>
    </div>
  );
};

export default SurveyBuilderPage;
